import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useDatabaseContent } from '@databases/database-state';
import { useFlags } from '@databases/flags';
import { type AddFlagProps } from '@databases/flags/types';
import { messages as globalMessages } from '@databases/intl/messages';
import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import { type UndoManager } from 'yjs';

import { getMessageByType } from '../helpers/messageHelper';
import { messages } from '../messages';
import { type PasteFlagParams, type PasteMessageType } from '../types';

export const useDisplayPasteFlags = (undoManager: UndoManager | undefined) => {
  const { formatMessage } = useIntl();
  const { addFlag, clearFlags } = useFlags();
  const client = useAnalytics();
  const { contentId } = useDatabaseContent();

  const sendAnalyticEvent = useCallback(
    async (contentId: string, actionType: PasteMessageType) => {
      await client.sendUndoEvent({
        action: 'paste',
        type: actionType as Exclude<
          PasteMessageType,
          | 'cannotReadPastedContent'
          | 'unexpectedDataType'
          | 'structureLocked'
          | 'readOnly'
          | 'entriesFiltered'
          | 'restrictedContentCantBeCopied'
        >,
        source: 'flags',
        contentId,
        databaseContainer: getDatabaseContainerType(),
      });
    },
    [client]
  );

  const handleUndo = useCallback(
    (actionType: PasteMessageType, undoManager: UndoManager) => {
      undoManager.undo();
      if (contentId) {
        sendAnalyticEvent(contentId, actionType);
      }
    },
    [contentId, sendAnalyticEvent]
  );

  /**
   * Returns actions for the flag
   *
   * @param undoManager - When defined, the undo action will be added to the actions
   */
  const getActions = useCallback(
    (pasteMessageType: PasteMessageType, undoManager: UndoManager | undefined) => {
      const result = [];
      if (undoManager) {
        result.push({
          content: formatMessage(globalMessages['button-message.undo-action']),
          onClick: () => {
            handleUndo(pasteMessageType, undoManager);
            clearFlags();
          },
        });
      }

      const dismissAction = {
        content: formatMessage(globalMessages['button-message.dismiss-action']),
        onClick: () => {
          clearFlags();
        },
      };
      result.push(dismissAction);
      return result;
    },
    [clearFlags, formatMessage, handleUndo]
  );

  const getFlagData = useCallback(
    (createPasteFlagParams: PasteFlagParams[]): AddFlagProps[] => {
      const result: AddFlagProps[] = [];
      for (const { type, actionType, options } of createPasteFlagParams) {
        const { title, description, excludeUndo } = getMessageByType(actionType);
        const messageValues =
          options && 'messageValues' in options ? options.messageValues : undefined;

        result.push({
          title: formatMessage(messages[title], messageValues),
          description: description
            ? formatMessage(messages[description], messageValues)
            : '',
          appearance: type,
          actions: getActions(actionType, excludeUndo ? undefined : undoManager),
        });
      }

      return result;
    },
    [formatMessage, getActions, undoManager]
  );

  return useCallback(
    (createPasteFlagParams: PasteFlagParams[]) => {
      const addFlagData = getFlagData(createPasteFlagParams);
      for (const flagData of addFlagData) {
        addFlag(flagData);
      }
    },
    [addFlag, getFlagData]
  );
};
